import type {
  BootstrapLatestUnreadEventPerFeed,
  BootstrapWorkspaceAccounts,
  BootstrapWorkspaceFeedGroupMemberships,
  BootstrapWorkspaceFeedGroups,
  BootstrapWorkspaceFeedItems,
  BootstrapWorkspaceFeedPermissions,
  BootstrapWorkspaceFeeds,
  BootstrapWorkspaceMemberships,
  BootstrapWorkspaceMembershipTags,
  BootstrapWorkspaceTags,
  BootstrapWorkspaceTemplates,
  WorkspaceMembership,
  WsAccount,
  WsCommandAlias,
  WsEvent,
  WsFeed,
  WsFeedGroup,
  WsFeedGroupMembership,
  WsItem,
  WsMembershipAvailability,
  WsPermission,
  WsTemplate,
} from "@storyboard/web-client";
import { createLogger } from "@/logger";

const log = createLogger("bootstrap");

import { client } from "@/config";
import { BootstrapEventsRegistry } from "@/models/BootstrapEventsRegistry";
import {
  bulkUpsertAccount,
  bulkUpsertFeed,
  bulkUpsertFeedGroup,
  bulkUpsertFeedGroupMembership,
  bulkUpsertItemAndCalculateMembership,
  bulkUpsertPermission,
  bulkUpsertTag,
  bulkUpsertTemplate,
  bulkUpsertWorkspaceCommandAlias,
  bulkUpsertWorkspaceMembership,
  bulkUpsertWorkspaceMembershipTag,
  bulkUpsertWsMembershipAvailability,
} from "@/models/repositories";
import { bulkUpsertAccountEvent } from "@/models/repositories/AccountEvent";
import { bulkUpsertFeedPreference } from "@/models/repositories/FeedPreference";
import { databaseResetService } from "@/services/DatabaseReset";

export type BootstrapResponses =
  | BootstrapWorkspaceFeedItems
  | BootstrapWorkspaceMemberships
  | BootstrapWorkspaceMembershipTags
  | BootstrapWorkspaceFeedPermissions
  | BootstrapWorkspaceFeeds
  | BootstrapWorkspaceTags
  | BootstrapWorkspaceTemplates
  | BootstrapWorkspaceAccounts
  | BootstrapWorkspaceFeedGroups
  | BootstrapWorkspaceFeedGroupMemberships
  | BootstrapLatestUnreadEventPerFeed;

export type CreateManyPayloads =
  | WorkspaceMembership[]
  | WsItem[]
  | WsPermission[]
  | WsFeed[]
  | WsTemplate[]
  | WsAccount[]
  | WsCommandAlias[]
  | WsFeedGroup[]
  | WsFeedGroupMembership[]
  | WsEvent[]
  | WsMembershipAvailability[];

export type BootstrapEventListType = {
  bootstrapFunctionName: string;
  bootstrapFunctionCallResponseName: string;
  createManyFunctionName: (values: any) => Promise<unknown>;
  tableName: string;
  feed?: boolean;
  wait?: boolean;
  pageSize?: number;
  mine?: boolean;
  recursive?: boolean;
};

const bulkUpsertItemsBootstrap = (values: WsItem[]) => bulkUpsertItemAndCalculateMembership(values, {});

const bulkUpsertAvailabilityBootstrap = (values: WsMembershipAvailability[]) =>
  bulkUpsertWsMembershipAvailability(values);

export const bootstrapItem = async ({
  bootstrapEvent,
  workspaceId,
  page,
  pageSize,
  mine,
  timestamp,
}: {
  bootstrapEvent: BootstrapEventListType;
  workspaceId: string;
  page: number;
  pageSize: number;
  mine: boolean;
  timestamp?: string;
}) => {
  try {
    const additionalResponse: BootstrapResponses = await client[bootstrapEvent?.bootstrapFunctionName]({
      workspaceId,
      page,
      pageSize,
      mine,
      timestamp,
    });
    const responseData = additionalResponse[bootstrapEvent?.bootstrapFunctionCallResponseName];
    log.info({
      key: bootstrapEvent?.bootstrapFunctionCallResponseName ?? "missing-name",
      count: responseData?.length ?? 0,
    });
    return responseData;
  } catch (error) {
    return Promise.reject(error);
  }
};

/**
 * Recursive api call that loads bootstrap event data and hydrates local db
 * If the response length matches the page size, it calls itself with the next page number
 * @param workspaceId
 * @param bootstrapEvent
 * @param pageSize
 * @param page
 */
export const loadBootstrapEventAndHydrateLocalDB = async ({
  workspaceId,
  bootstrapEvent,
  pageSize,
  page,
  moreData,
}: {
  workspaceId: string;
  bootstrapEvent: BootstrapEventListType;
  pageSize: number;
  page: number;
  moreData?: any[];
}) => {
  const payload = {
    bootstrapEvent,
    workspaceId,
    page,
    pageSize,
    mine: true,
    timestamp: undefined,
  };

  try {
    const response = await bootstrapItem(payload);
    if (response && response.length > 0) {
      await bootstrapEvent.createManyFunctionName(response);
    }
    if (response?.length === pageSize) {
      const combined = moreData ? [...moreData, ...response] : response;
      return await loadBootstrapEventAndHydrateLocalDB({
        bootstrapEvent,
        workspaceId,
        page: page + 1,
        pageSize: pageSize,
        moreData: combined,
      });
    } else if (moreData) {
      const moreDataArray = Array.isArray(moreData) ? moreData : [];
      const responseDataArray = Array.isArray(response) ? response : [];
      return [...moreDataArray, ...responseDataArray];
    } else {
      return response;
    }
  } catch (error) {
    return Promise.reject(error);
  }
};

export const bootstrapWorkspaceMemberships = {
  bootstrapFunctionName: "bootstrapWorkspaceMemberships",
  createManyFunctionName: bulkUpsertWorkspaceMembership,
  bootstrapFunctionCallResponseName: "workspaceMemberships",
  tableName: "workspaceMembership",
};

export const bootstrapWorkspaceFeedPermissions = {
  bootstrapFunctionName: "bootstrapWorkspaceFeedPermissions",
  createManyFunctionName: bulkUpsertPermission,
  bootstrapFunctionCallResponseName: "permissions",
  tableName: "permission",
  mine: true,
};

export const bootstrappedApiCalls: BootstrapEventListType[] = [
  {
    bootstrapFunctionName: "bootstrapWorkspaceMembershipAvailabilities",
    createManyFunctionName: bulkUpsertAvailabilityBootstrap,
    bootstrapFunctionCallResponseName: "membershipAvailabilities",
    tableName: "wsMembershipAvailability",
    mine: true,
  },
  bootstrapWorkspaceFeedPermissions,
  {
    bootstrapFunctionName: "bootstrapWorkspaceFeeds",
    createManyFunctionName: bulkUpsertFeed,
    bootstrapFunctionCallResponseName: "feeds",
    tableName: "feed",
    feed: true,
  },
  {
    bootstrapFunctionName: "bootstrapWorkspaceNonAliasFeedLatestItems",
    createManyFunctionName: bulkUpsertItemsBootstrap,
    bootstrapFunctionCallResponseName: "items",
    tableName: "item",
  },
  {
    bootstrapFunctionName: "bootstrapWorkspaceAliasFeedLatestOwnerItems",
    createManyFunctionName: bulkUpsertItemsBootstrap,
    bootstrapFunctionCallResponseName: "items",
    tableName: "item",
  },
  bootstrapWorkspaceMemberships,
  {
    bootstrapFunctionName: "bootstrapWorkspaceTags",
    createManyFunctionName: bulkUpsertTag,
    bootstrapFunctionCallResponseName: "tags",
    tableName: "tag",
  },
  {
    bootstrapFunctionName: "bootstrapWorkspaceMembershipTags",
    createManyFunctionName: bulkUpsertWorkspaceMembershipTag,
    bootstrapFunctionCallResponseName: "workspaceMembershipTags",
    tableName: "workspaceMembershipTag",
    recursive: false,
  },
  {
    bootstrapFunctionName: "bootstrapWorkspaceTemplates",
    createManyFunctionName: bulkUpsertTemplate,
    bootstrapFunctionCallResponseName: "templates",
    tableName: "template",
  },
  {
    bootstrapFunctionName: "bootstrapWorkspaceAccounts",
    createManyFunctionName: bulkUpsertAccount,
    bootstrapFunctionCallResponseName: "accounts",
    tableName: "account",
  },
  {
    bootstrapFunctionName: "bootstrapWorkspaceFeedGroups",
    createManyFunctionName: bulkUpsertFeedGroup,
    bootstrapFunctionCallResponseName: "feedGroups",
    tableName: "feedGroup",
  },
  {
    bootstrapFunctionName: "bootstrapWorkspaceFeedGroupMemberships",
    createManyFunctionName: bulkUpsertFeedGroupMembership,
    bootstrapFunctionCallResponseName: "feedGroupMemberships",
    tableName: "feedGroupMembership",
  },
  {
    bootstrapFunctionName: "bootstrapWorkspaceCommandAliases",
    createManyFunctionName: bulkUpsertWorkspaceCommandAlias,
    bootstrapFunctionCallResponseName: "commandAliases",
    tableName: "workspaceCommandAlias",
  },
  {
    bootstrapFunctionName: "latestUnreadEventPerFeed",
    createManyFunctionName: bulkUpsertAccountEvent,
    bootstrapFunctionCallResponseName: "events",
    tableName: "accountEvent",
  },
  {
    bootstrapFunctionName: "latestUnreadEventPerFeedForOrganizers",
    createManyFunctionName: bulkUpsertAccountEvent,
    bootstrapFunctionCallResponseName: "events",
    tableName: "accountEvent",
  },
  {
    bootstrapFunctionName: "bootstrapWorkspaceFeedPreferences",
    createManyFunctionName: bulkUpsertFeedPreference,
    bootstrapFunctionCallResponseName: "feedPreferences",
    tableName: "feedPreference",
    mine: true,
  },
];

export const fetchLatestBootstrapEventDate = async (name: string): Promise<Date | undefined> => {
  const registry = BootstrapEventsRegistry.getInstance();
  const date = await registry.getLatestEventDate(name);
  return date;
};

export const updateLatestBootstrapEventDate = async (name: string): Promise<void> => {
  const registry = BootstrapEventsRegistry.getInstance();
  await registry.touchUpdate(name);
};

export const insertBootstrapEvent = async (name: string, tableName: string) => {
  const registry = BootstrapEventsRegistry.getInstance();
  const metadata = await registry.markBootstrap(name, tableName);
  return metadata;
};

export const forceReload = async () => {
  try {
    await databaseResetService.hardReboot();
  } catch {
    return Promise.reject({ message: "Error forcing reload" });
  }
};
